/**
 * Sidebar Component
 * 대시보드 사이드바 네비게이션
 *
 * @description
 * - 역할 기반 메뉴 필터링 (본사/협력사/라이더)
 * - 협력사 타입별 메뉴 제어 (직영D/가맹F)
 * - 아코디언 스타일 서브메뉴
 * - 3단계 메뉴 지원 (children)
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { getItem } from "@/shared/utils/storage";
import { NAV_ITEMS } from '@/features/dashboard/config/navigation.config';
import type { NavItem, NavSubItem } from "@/features/dashboard/config/navigation.config";
import { useActiveMaintenance } from '@/features/system/maintenance/hooks/useMaintenanceData';
import '../../../styles/Sidebar.css';

// ============================================================
// 타입 정의
// ============================================================
interface SidebarProps {
    onPageChange: (page: string) => void;
    currentPage?: string;
}

// ============================================================
// 상수 정의
// ============================================================
const USER_TYPES = {
    HEADQUARTERS: 'HEADQUARTERS',
    SUPPLIER: 'SUPPLIER',
    RIDER: 'RIDER',
} as const;

/** 협력사 타입 */
const SUPPLIER_TYPES = {
    DIRECT: 'D',    // 직영
    FRANCHISE: 'F'  // 가맹
} as const;

/** 권한 코드 */
const PRM_CODES = {
    ADMIN: 'A',     // 관리자
} as const;

/** 기본 권한 코드 (커스텀 권한이 아닌 것들) */
const STANDARD_PERMISSION_CODES = new Set(['A', 'C', 'S', 'R']);

// ============================================================
// 컴포넌트 구현
// ============================================================
const Sidebar: React.FC<SidebarProps> = ({ onPageChange, currentPage }) => {
    const [activeMenu, setActiveMenu] = useState<string | null>('dashboard');
    const [activeSubMenu, setActiveSubMenu] = useState<string | null>(null);  // 3단계 메뉴용
    const [activePage, setActivePage] = useState<string>('dashboard');
    const [userType, setUserType] = useState<string>('');
    const [suppType, setSuppType] = useState<string>(SUPPLIER_TYPES.DIRECT);
    const [prmCode, setPrmCode] = useState<string>('');
    const [accessPages, setAccessPages] = useState<Set<string>>(new Set());

    // 점검 상태 조회
    const { isMaintenance } = useActiveMaintenance();

    // 점검 중 접근 가능한 페이지 목록 (대시보드만 허용)
    const allowedPagesDuringMaintenance = useMemo(() => new Set([
        'dashboard'
    ]), []);

    // 점검 중 비활성화할 메뉴 목록 (대시보드 제외 모든 메뉴)
    const disabledMenusDuringMaintenance = useMemo(() => new Set([
        'notice',
        'rider',
        'partner',
        'settlement'
    ]), []);

    // 협력사이고 점검 중이면 특정 페이지만 접근 가능 (localStorage에서 직접 읽어서 초기 렌더링 시에도 정확하게 판단)
    const currentUserType = getItem<string>('USER_TYPE') || userType;
    const isMaintenanceMode = currentUserType === USER_TYPES.SUPPLIER && isMaintenance;
    
    // 페이지별 접근 가능 여부 확인 함수
    const isPageAccessible = useCallback((page: string | undefined) => {
        if (!page) return false;
        if (!isMaintenanceMode) return true;
        return allowedPagesDuringMaintenance.has(page);
    }, [isMaintenanceMode, allowedPagesDuringMaintenance]);

    // 메뉴별 접근 가능 여부 확인 함수
    const isMenuAccessible = useCallback((menuId: string) => {
        if (!isMaintenanceMode) return true;
        return !disabledMenusDuringMaintenance.has(menuId);
    }, [isMaintenanceMode, disabledMenusDuringMaintenance]);

    /**
     * 사용자 타입 및 협력사 타입 초기화
     */
    useEffect(() => {
        const userTypeValue = getItem<string>('USER_TYPE') || '';
        setUserType(userTypeValue);

        // PRM_CODE 읽기
        const prmCodeValue = getItem<string>('PRM_CODE') || '';
        setPrmCode(prmCodeValue);

        // ACCESS_PAGES 읽기 (커스텀 권한의 경우)
        const accessPagesValue = getItem<string>('ACCESS_PAGES') || '';
        if (accessPagesValue) {
            const pagesSet = new Set(accessPagesValue.split(',').map(p => p.trim()));
            setAccessPages(pagesSet);
        }

        // 협력사 사용자일 때만 SUPP_TYPE 읽기
        if (userTypeValue === USER_TYPES.SUPPLIER) {
            const suppTypeValue = getItem<string>('SUPP_TYPE');
            setSuppType(suppTypeValue?.trim() || SUPPLIER_TYPES.DIRECT);
        }
    }, []);

    /**
     * currentPage 변경 시 활성 메뉴 업데이트
     */
    useEffect(() => {
        if (currentPage && currentPage !== activePage) {
            setActivePage(currentPage);

            // 해당 페이지의 부모 메뉴 찾기
            for (const item of NAV_ITEMS) {
                if (item.submenu) {
                    for (const sub of item.submenu) {
                        // 3단계 메뉴 확인
                        if (sub.children) {
                            const hasMatch = sub.children.some(child => child.page === currentPage);
                            if (hasMatch) {
                                setActiveMenu(item.id);
                                setActiveSubMenu(sub.id);
                                return;
                            }
                        }
                        // 2단계 메뉴 확인
                        if (sub.page === currentPage) {
                            setActiveMenu(item.id);
                            setActiveSubMenu(null);
                            return;
                        }
                    }
                }
            }
        }
    }, [currentPage, activePage]);

    /**
     * 사용자 권한에 따른 서브메뉴 필터링
     */
    const filterSubmenu = useCallback((menuId: string, submenu: NavSubItem[]): NavSubItem[] => {
        const isHeadquarters = userType === USER_TYPES.HEADQUARTERS;
        const isSupplier = userType === USER_TYPES.SUPPLIER;
        const isFranchise = suppType === SUPPLIER_TYPES.FRANCHISE;

        // 가맹 협력사의 partner 메뉴
        if (menuId === 'partner' && isSupplier && isFranchise) {
            return submenu.filter(item => item.id === 'partner_status');
        }

        // partner 메뉴 수수료 권한 필터링
        if (menuId === 'partner') {
            return submenu.map(item => {
                // 협력사 수수료 (3단계 메뉴)
                if (item.id === 'partner_fee' && item.children) {
                    const filteredChildren = item.children.filter(child => {
                        // 협력사 → 본사: 본사만 접근 가능
                        if (child.id === 'partner_fee_to_head') {
                            return isHeadquarters;
                        }
                        // 라이더 → 협력사: 본사 및 직영 협력사 접근 가능
                        if (child.id === 'rider_fee_to_supplier') {
                            return isHeadquarters || (isSupplier && !isFranchise);
                        }
                        return true;
                    });

                    // 하위 메뉴가 없으면 부모 메뉴도 숨김
                    if (filteredChildren.length === 0) {
                        return null;
                    }

                    return { ...item, children: filteredChildren };
                }
                return item;
            }).filter((item): item is NavSubItem => item !== null);
        }

        // settlement 메뉴 필터링
        if (menuId === 'settlement') {
            return submenu
                .filter(item => {
                    switch (item.id) {
                        case 'rider_work_status':
                        case 'transaction_history':
                        case 'virtual_account':
                        case 'partner_deduction_history':
                        case 'fee_management':
                        case 'transfer_management':
                            return isHeadquarters;
                        case 'rider_deduction_history':
                            return isSupplier && !isFranchise
                        default:
                            return true;
                    }
                })
                .map(item => {
                    // 본사용 라벨 변경
                    if (item.id === 'partner_deduction_history' && isHeadquarters) {
                        return { ...item, label: '본사 일차감내역' };
                    }
                    // 일차감 3단계 메뉴 필터링: 협력사 → 라이더 → 협력사만 표시
                    if (item.id === 'deduction' && item.children && isSupplier) {
                        return {
                            ...item,
                            children: item.children.filter(child =>
                                child.id !== 'deduction_partner_to_head' && child.id !== 'deduction_rider_to_head'
                            )
                        };
                    }
                    // 프로모션 3단계 메뉴 필터링: 협력사 → 협력사 → 라이더만 표시
                    if (item.id === 'promotion' && item.children && isSupplier) {
                        return {
                            ...item,
                            children: item.children.filter(child =>
                                child.id !== 'promotion_head_to_supplier' && child.id !== 'promotion_head_to_rider'
                            )
                        };
                    }
                    return item;
                });
        }

        // rider 메뉴 필터링
        if (menuId === 'rider') {
            return submenu.filter(item => {
                if (item.id === 'rider_work_status') {
                    return isHeadquarters;
                }
                return true;
            });
        }

        // policy 메뉴 필터링 (본사만 접근 가능)
        if (menuId === 'policy') {
            return isHeadquarters ? submenu : [];
        }

        // system 메뉴 필터링 (본사만 접근 가능)
        if (menuId === 'system') {
            return isHeadquarters ? submenu : [];
        }

        return submenu;
    }, [userType, suppType, prmCode]);

    /**
     * 사용자 타입에 따라 필터링된 네비게이션 아이템
     */
    const filteredNavItems = useMemo((): NavItem[] => {
        const isRider = userType === USER_TYPES.RIDER;
        const isSupplier = userType === USER_TYPES.SUPPLIER;
        const isFranchise = suppType === SUPPLIER_TYPES.FRANCHISE;
        const hasCustomPermission = prmCode && !STANDARD_PERMISSION_CODES.has(prmCode);
        const hasAccessPages = accessPages.size > 0;

        return NAV_ITEMS
            .filter(item => {
                // 라이더: 모든 메뉴 숨김
                if (isRider) return false;

                // 커스텀 권한 + accessPages가 있는 경우: accessPages 기반 필터링
                if (hasCustomPermission && hasAccessPages) {
                    // 해당 메뉴 그룹에 접근 가능한 서브메뉴가 있는지 확인
                    const hasAccessibleSubmenu = item.submenu?.some(sub => {
                        // 3단계 메뉴 확인
                        if (sub.children) {
                            return sub.children.some(child => child.page && accessPages.has(child.page));
                        }
                        return sub.page && accessPages.has(sub.page);
                    });
                    return hasAccessibleSubmenu;
                }

                // 가맹 협력사: 대시보드, 공지사항만 표시
                if (isSupplier && isFranchise) {
                    return item.id === 'dashboard' || item.id === 'notice';
                }

                // 직영 협력사: 시스템 관리, 정책 관리 메뉴 숨김 (공지사항은 독립 메뉴로 접근)
                if (isSupplier && (item.id === 'system' || item.id === 'policy')) {
                    return false;
                }

                // 본사/관리자: 전체 메뉴 표시
                return true;
            })
            .map(item => {
                if (!item.submenu) return item;

                // 커스텀 권한 + accessPages가 있는 경우: 서브메뉴도 accessPages 기반 필터링
                if (hasCustomPermission && hasAccessPages) {
                    return {
                        ...item,
                        submenu: item.submenu.filter(sub => {
                            // 3단계 메뉴 확인
                            if (sub.children) {
                                return sub.children.some(child => child.page && accessPages.has(child.page));
                            }
                            return sub.page && accessPages.has(sub.page);
                        }).map(sub => {
                            // 3단계 메뉴 필터링
                            if (sub.children) {
                                return {
                                    ...sub,
                                    children: sub.children.filter(child => child.page && accessPages.has(child.page))
                                };
                            }
                            return sub;
                        })
                    };
                }

                return {
                    ...item,
                    submenu: filterSubmenu(item.id, item.submenu)
                };
            });
    }, [userType, suppType, prmCode, accessPages, filterSubmenu]);

    /**
     * 메뉴 클릭 핸들러
     */
    const handleMenuClick = useCallback((menuId: string) => {
        setActiveMenu(prev => prev === menuId ? null : menuId);
        setActiveSubMenu(null);  // 1단계 메뉴 변경 시 3단계 메뉴 닫기
    }, []);

    /**
     * 서브메뉴 (3단계 부모) 클릭 핸들러
     */
    const handleSubMenuClick = useCallback((subMenuId: string, e: React.MouseEvent) => {
        e.preventDefault();
        setActiveSubMenu(prev => prev === subMenuId ? null : subMenuId);
    }, []);

    const handlePageClick = useCallback((page: string) => {
        if (!isPageAccessible(page)) return;
        setActivePage(page);
        onPageChange(page);
    }, [onPageChange, isPageAccessible]);

    return (
        <aside className="sidebar">
            <div className="sidebar-header">
                <svg className="sidebar-logo" xmlns="http://www.w3.org/2000/svg" viewBox="130 145 690 85">
                    <g>
                        <path fill="#05CD82" d="M178.56,188.53l-13.84-31.51h-23.98l27.99,57.89h18.99l28.65-57.89h-23.97l-13.84,31.51h0Z"/>
                        <path fill="#05CD82" d="M279.95,166.82c0-5.4-4.69-9.8-10.46-9.8h-51.33v57.89h20.92v-24.43l26.08,24.43h14.79v-13.86l-26.08-24.43h15.62c5.77,0,10.46-4.4,10.46-9.8"/>
                        <path fill="#05CD82" d="M314.38,199.69c-7.62,0-13.82-6.16-13.82-13.72s6.2-13.72,13.82-13.72,13.82,6.16,13.82,13.72-6.2,13.72-13.82,13.72ZM314.38,153.86c-18.99,0-34.44,14.41-34.44,32.11s15.45,32.11,34.44,32.11,34.43-14.41,34.43-32.11-15.45-32.11-34.43-32.11Z"/>
                        <path fill="#05CD82" d="M385.99,199.69c-7.62,0-13.82-6.16-13.82-13.72s6.2-13.72,13.82-13.72,13.83,6.16,13.83,13.72-6.2,13.72-13.83,13.72ZM385.99,153.86c-18.99,0-34.43,14.41-34.43,32.11s15.45,32.11,34.43,32.11,34.44-14.41,34.44-32.11-15.45-32.11-34.44-32.11Z"/>
                        <path fill="#05CD82" d="M467.59,184.77l-23.17-27.74h-20.28v57.89h20.92v-27.74l23.17,27.74h20.27v-57.89h-20.92v27.74h.01Z"/>
                        <path fill="#05CD82" d="M526.64,218.08c17.76,0,32.21-14.41,32.21-32.11,0-1.95-.18-3.85-.51-5.71h-32.83v12.96h13.66c-1.61,2.91-5.34,6.51-11.99,6.51-4.09,0-7.56-1.37-10.42-4.09-2.86-2.73-4.28-6.02-4.28-9.86s1.36-7.12,4.09-9.84c2.72-2.72,6.04-4.07,9.95-4.07,2.42,0,4.69.56,6.79,1.68,1.17.62,2.2,1.36,3.1,2.2l16.92-10.13c-2.53-2.9-5.6-5.35-9.22-7.35-5.36-2.95-11.22-4.42-17.58-4.42-9.56,0-17.67,3.18-24.33,9.52-6.67,6.35-9.99,13.87-9.99,22.59,0,17.71,15.45,32.11,34.44,32.11h0Z"/>
                    </g>
                    <g>
                        <path fill="#6B6B6B" d="M801.36,187.28c-.4-2.85-2.93-4.68-6.5-4.68-3.33,0-5.94,1.51-5.86,3.73-.08,1.66,1.19,3.01,4.91,3.72l8.16,1.59c8.56,1.66,12.68,5.39,12.76,11.65-.08,8.8-8.16,14.51-19.82,14.51-12.29,0-19.5-5.47-20.37-14.03h13.32c.47,3.09,3.17,4.76,7.13,4.76,3.65,0,6.1-1.43,6.18-3.8-.08-1.9-1.74-3.09-5.47-3.88l-7.37-1.35c-8.48-1.59-12.76-5.87-12.68-12.44-.08-8.56,7.21-13.63,18.86-13.63s18.63,5.31,19.18,13.87h-12.44v-.02Z"/>
                        <path fill="#6B6B6B" d="M745.44,217v-43.04h13v7.77h.4c1.59-5.63,5.39-8.4,10.15-8.4,1.27,0,2.7.24,3.8.48v11.65c-1.27-.4-3.65-.63-5.31-.63-4.99,0-8.64,3.49-8.64,8.64v23.54h-13.4Z"/>
                        <path fill="#6B6B6B" d="M719.88,217.79c-13.47,0-21.72-8.4-21.72-22.11s8.32-22.27,21.24-22.27c12.05,0,20.77,7.69,20.77,21.96v3.41h-28.85v.24c0,5.55,3.41,9.12,8.8,9.12,3.65,0,6.34-1.59,7.45-4.12h12.44c-1.59,8.4-8.96,13.79-20.13,13.79v-.02ZM711.32,190.92h16.41c-.08-4.6-3.41-7.85-8.01-7.85s-8.2,3.37-8.4,7.85h0Z"/>
                        <path fill="#6B6B6B" d="M667.05,217.63c-9.59,0-17.28-7.53-17.28-22.11s8.16-22.11,17.2-22.11c6.9,0,10.54,4.04,12.21,7.93h.32v-21.72h13.32v57.38h-13.24v-6.98h-.4c-1.74,3.88-5.55,7.61-12.13,7.61ZM671.57,207.17c5.15,0,8.16-4.6,8.16-11.73s-2.93-11.57-8.16-11.57-8.08,4.6-8.08,11.57,2.93,11.73,8.08,11.73Z"/>
                        <path fill="#6B6B6B" d="M630.95,217v-43.04h13.4v43.04h-13.4ZM637.68,168.81c-3.8,0-6.89-2.85-6.89-6.42s3.09-6.42,6.89-6.42,6.98,2.85,6.98,6.42-3.17,6.42-6.98,6.42Z"/>
                        <path fill="#6B6B6B" d="M580.73,217v-57.38h23.46c12.92,0,20.85,7.29,20.85,19.02,0,7.89-3.61,13.55-9.91,16.41l11.97,21.96h-14.82l-10.54-19.74h-7.53v19.74h-13.48ZM594.21,186.48h7.13c6.34,0,9.75-2.46,9.75-7.85s-3.41-8.09-9.75-8.09h-7.13v15.93h0Z"/>
                    </g>
                </svg>
            </div>

            {/* 네비게이션 메뉴 */}
            <nav className="sidebar-nav">
                {filteredNavItems.map((item) => {
                    const isMenuDisabled = !isMenuAccessible(item.id);
                    return (
                        <div key={item.id} className="nav-group">
                            {/* 메인 메뉴 */}
                            <a
                                href="#"
                                className={`nav-link ${activeMenu === item.id ? 'active' : ''} ${isMenuDisabled ? 'disabled' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (isMenuDisabled) return;
                                    handleMenuClick(item.id);
                                }}
                            >
                                <div className="nav-icon">
                                    <i className={`fas ${item.icon}`}></i>
                                </div>
                                <span>{item.label}</span>
                                <div className={`nav-arrow ${activeMenu === item.id ? 'open' : ''}`}>
                                    <i className="fas fa-chevron-right"></i>
                                </div>
                            </a>

                            {/* 서브메뉴 */}
                            {item.submenu && (
                                <div className={`nav-submenu ${activeMenu === item.id ? 'open' : ''}`}>
                                    {item.submenu.map((subItem) => (
                                        <div key={subItem.id}>
                                            {/* 3단계 메뉴가 있는 경우 */}
                                            {subItem.children ? (
                                                <>
                                                    <a
                                                        href="#"
                                                        className={`nav-sublink has-children ${activeSubMenu === subItem.id ? 'expanded' : ''} ${isMenuDisabled ? 'disabled' : ''}`}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            if (isMenuDisabled) return;
                                                            handleSubMenuClick(subItem.id, e);
                                                        }}
                                                    >
                                                        <span>{subItem.label}</span>
                                                        <i className={`fas fa-chevron-down submenu-arrow ${activeSubMenu === subItem.id ? 'open' : ''}`}></i>
                                                    </a>
                                                    {/* 3단계 메뉴 */}
                                                    <div className={`nav-submenu-children ${activeSubMenu === subItem.id ? 'open' : ''}`}>
                                                        {subItem.children.map((child) => {
                                                            const isAccessible = isPageAccessible(child.page);
                                                            return (
                                                                <a
                                                                    key={child.id}
                                                                    href="#"
                                                                    className={`nav-sublink nav-sublink-child ${activePage === child.page ? 'active' : ''} ${!isAccessible || isMenuDisabled ? 'disabled' : ''}`}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        if (isMenuDisabled) return;
                                                                        if (child.page) handlePageClick(child.page);
                                                                    }}
                                                                >
                                                                    {child.label}
                                                                </a>
                                                            );
                                                        })}
                                                    </div>
                                                </>
                                            ) : (
                                                /* 일반 서브메뉴 (페이지 링크) */
                                                <a
                                                    href="#"
                                                    className={`nav-sublink ${activePage === subItem.page ? 'active' : ''} ${!isPageAccessible(subItem.page) || subItem.disabled || isMenuDisabled ? 'disabled' : ''}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        if (subItem.disabled || isMenuDisabled) return;
                                                        if (subItem.page) handlePageClick(subItem.page);
                                                    }}
                                                >
                                                    {subItem.label}
                                                </a>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </nav>
        </aside>
    );
};

export default Sidebar;
